'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ManageSubscription } from '../components/ManageSubscription';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';

interface UserSettings {
  creditsConsumed?: number;
  creditLimit?: number;
  subscription?: string;
}

export default function SubscriptionPage() {
  const { toast } = useToast();
  const { apiCall } = useAuthenticatedApi();
  const { subscriptionSpend, setSubscriptionSpend, subscriptionLimit, setSubscriptionLimit } = useSubscription();
  const [isLoading, setIsLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<string>();

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await apiCall<UserSettings>('/user', 'GET');
        if (response) {
          setSubscriptionSpend(response.creditsConsumed || 0);
          setSubscriptionLimit(response.creditLimit || 0);
          setCurrentSubscription(response.subscription);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load subscription information',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [apiCall, setSubscriptionLimit, setSubscriptionSpend, toast]);

  const creditUsagePercentage = (subscriptionSpend / subscriptionLimit) * 100;

  return (
    <div className="container mx-auto p-6">
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Subscription</h2>
            <p className="text-muted-foreground">Manage your subscription and credit packages</p>
          </div>
        </div>

        {isLoading ? (
          <div className="container mx-auto py-10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Credits Usage</CardTitle>
                <CardDescription>Monitor your current credit usage and limits</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      {subscriptionSpend} / {subscriptionLimit} credits used
                    </div>
                    <div className="text-sm text-muted-foreground">{creditUsagePercentage.toFixed(1)}%</div>
                  </div>
                  <Progress value={(subscriptionSpend / subscriptionLimit) * 100} className="h-2" />
                </div>
                <ManageSubscription currentSubscription={currentSubscription} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
